import { FunctionComponent, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import AppLogo from "@/assets/images/favicon.png";
import { TokenStatus } from "@/enum";
import { ROUTE_CONFIG } from "@/routes/config";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { useAppSelector } from "@/redux/hooks";

import { authSelector } from "../../redux/auth.slice";
import { useResendVerificationEmailMutation, useVerifyEmailMutation } from "../../redux/auth.api";

/**
 * Landing page for the link in the verification email (PREC-3876).
 *
 * The API has always mailed `${webAppDomain}/verify-email?token=…`, but the route
 * did not exist, so the token was never consumed and `emailVerifiedAt` stayed null
 * for every self-signup. The route is listed in `PUBLIC_PAGES_AFTER_LOGIN` so a
 * logged-in user landing here consumes their token instead of being bounced into
 * the app.
 */
export const VerifyEmail: FunctionComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { token: authToken } = useAppSelector(authSelector);

	const [verifyEmail, { data, isLoading, isError }] = useVerifyEmailMutation();
	const [resendVerificationEmail, { isLoading: isResending, isSuccess: hasResent }] =
		useResendVerificationEmailMutation();

	const token = searchParams.get("token") ?? "";

	// The token is single-use: StrictMode's double effect would burn it on the
	// second call and report INVALID for a link that just worked.
	const hasRequestedRef = useRef(false);

	useEffect(() => {
		if (!token || hasRequestedRef.current) {
			return;
		}

		hasRequestedRef.current = true;
		verifyEmail({ token });
	}, [token, verifyEmail]);

	const isVerified = data?.status === TokenStatus.USED;
	const isPending = !!token && (isLoading || (!data && !isError));

	const failureMessage = (): string => {
		if (!token) {
			return t("verifyEmail.missingToken");
		}

		if (data?.status === TokenStatus.EXPIRED) {
			return t("verifyEmail.expired");
		}

		return t("verifyEmail.invalid");
	};

	return (
		<div className="w-full h-full flex flex-row items-center justify-center">
			<div className="flex flex-col bg-white p-6 rounded-[20px] w-[428px] max-w-[100%] shadow-[0px_0px_80px_0px_rgba(171,178,217,0.35)] items-center justify-center">
				<img alt="logo" className="w-[48px] h-[48px] mb-4" src={AppLogo} />

				{isPending && (
					<div className="text-center text-md font-bold mb-4">{t("verifyEmail.pending")}</div>
				)}

				{!isPending && isVerified && (
					<>
						<div className="text-center text-md font-bold mb-4">{t("verifyEmail.title")}</div>
						<div className="text-center text-ssm font-normal mb-8">
							{t("verifyEmail.description")}
						</div>
						<div className="w-full">
							<Button
								size={ButtonSize.ML}
								title={t("verifyEmail.continue")}
								onClick={() => navigate(authToken ? ROUTE_CONFIG.LISTS : ROUTE_CONFIG.LOGIN)}
							/>
						</div>
					</>
				)}

				{!isPending && !isVerified && (
					<>
						<div className="text-center text-md font-bold mb-4">{t("verifyEmail.failedTitle")}</div>
						<div className="text-center text-ssm font-normal mb-8">{failureMessage()}</div>

						{hasResent ? (
							<div className="text-center text-ssm font-normal mb-4" data-testid="resent-hint">
								{t("verifyEmail.resent")}
							</div>
						) : (
							authToken && (
								<div className="w-full mb-2">
									<Button
										isLoading={isResending}
										size={ButtonSize.ML}
										title={t("verifyEmail.resend")}
										onClick={() => resendVerificationEmail()}
									/>
								</div>
							)
						)}

						<div className="w-full">
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								size={ButtonSize.ML}
								title={t("verifyEmail.backToLogin")}
								onClick={() => navigate(ROUTE_CONFIG.LOGIN)}
							/>
						</div>
					</>
				)}
			</div>
		</div>
	);
};
